import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { closeModal } from '@/store/actions/core.actions';

import { advancedRoleStatus, IAdvancedRoleMetadata } from '@/features/roles/store/models/advancedRole.model';
import { ICursorElements } from '@/store/types/search.types';
import {
  ISupportUser,
  ISupportUserFilter,
  ISupportUserRequest,
  supportUserLockReason,
  supportUserStatus,
  TFavorite,
} from '@/features/dashboard-users/store/support-user.model';
import { TAppError } from '@/store/types/base.state';
import { TCoreAction } from '@/store/types/modals.types';
import { TAuthAction, IAuthState } from '@/store/types/auth.types';
import { IDependencies } from '@/store/configuration';
import { TAppState } from '@/store/reducers/reducers';
import { SupportUserService } from '@/features/dashboard-users/services/SupportUserService';
import {
  TSupportUserAction,
  ISetCurrentSupportUserAction,
  SupportUserActionTypes,
} from '@/features/dashboard-users/store/types/support-user.types';
import { getAdvandeRoleFromScopes } from '@/hooks/useAccessControl';
import { getCurrentSupportUser } from '@/features/dashboard-users/helpers/getCurrentSupportUser';
import { getSupportUserScopes } from '@/features/roles/helpers/getSupportUserScopes';
import { IChangePassword } from '@/features/dashboard-users/store/types/password.types';
import { Auth2FaService } from '@/features/2fa/services/Auth2FaService';

export const getSupportUsers = (
  filter?: ISupportUserFilter,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction> => {
  const request = (): TSupportUserAction => ({
    type: SupportUserActionTypes.SEARCH_SUPPORT_USERS,
  });

  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.SEARCH_SUPPORT_USERS_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/users/support.html#creation',
  });

  const success = (supportUsers: ICursorElements<ISupportUser>): TSupportUserAction => ({
    type: SupportUserActionTypes.SEARCH_SUPPORT_USERS_SUCCESS,
    shouldSkipToaster: true,
    supportUsers,
  });

  return async (dispatch: ThunkDispatch<TAppState, IDependencies, TSupportUserAction>, _): Promise<void> => {
    dispatch(request());
    try {
      const supportUsers = await SupportUserService.getSupportUsers(filter);
      dispatch(success(supportUsers));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getSuspendedSupportUsers = (
  filter?: Pick<ISupportUserFilter, 'cursor'>,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction> => {
  const request = (): TSupportUserAction => ({
    type: SupportUserActionTypes.SEARCH_SUSPENDED_SUPPORT_USERS,
  });

  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.SEARCH_SUSPENDED_SUPPORT_USERS_FAIL,
    error,
    // documentation: 'https://docs.treezor.com/guide/users/support.html#creation',
  });

  const success = (supportUsers: ICursorElements<ISupportUser>): TSupportUserAction => ({
    type: SupportUserActionTypes.SEARCH_SUSPENDED_SUPPORT_USERS_SUCCESS,
    shouldSkipToaster: true,
    supportUsers,
  });

  return async (dispatch: ThunkDispatch<TAppState, IDependencies, TSupportUserAction>, _): Promise<void> => {
    dispatch(request());
    try {
      const supportUsers = await SupportUserService.getSupportUsers({
        status: supportUserStatus.LOCK,
        lockReason: supportUserLockReason.TEMPORARY_BLOCK,
        ...(filter ? { cursor: filter.cursor } : {}),
      });
      dispatch(success(supportUsers));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const setCurrentSupportUser = (user: ISupportUser): ISetCurrentSupportUserAction => ({
  type: SupportUserActionTypes.SET_CURRENT_SUPPORT_USER,
  shouldSkipToaster: true,
  user,
});

export const addSupportUser = (
  supportUser: ISupportUserRequest,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction | TCoreAction> => {
  const request = (): TSupportUserAction => ({
    type: SupportUserActionTypes.ADD_SUPPORT_USER,
  });

  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.ADD_SUPPORT_USER_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/users/support.html#creation',
  });

  const success = (supportUser: ISupportUser): TSupportUserAction => ({
    type: SupportUserActionTypes.ADD_SUPPORT_USER_SUCCESS,
    supportUser,
  });

  return async (
    dispatch: ThunkDispatch<TAppState, IDependencies, TSupportUserAction | TCoreAction>,
    _,
  ): Promise<void> => {
    dispatch(request());
    try {
      let createdSupportUser = await SupportUserService.addSupportUser(supportUser);

      if (supportUser.metadata && createdSupportUser.id) {
        const updatedSupportUser = await SupportUserService.updateSupportUserMetadata(
          createdSupportUser.id,
          supportUser.metadata,
        );
        createdSupportUser = { ...updatedSupportUser };
      }

      dispatch(success(createdSupportUser));
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updateSupportUser = (
  supportUser: ISupportUserRequest,
  shouldRestore?: boolean,
  onFinish?: () => void,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction | TCoreAction> => {
  const request = (): TSupportUserAction => ({
    type: SupportUserActionTypes.UPDATE_SUPPORT_USER,
    id: supportUser.id!,
  });

  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.UPDATE_SUPPORT_USER_FAIL,
    id: supportUser.id!,
    error,
    documentation: 'https://docs.treezor.com/guide/users/support.html#creation',
  });

  const success = (supportUser: ISupportUser): TSupportUserAction => ({
    type: SupportUserActionTypes.UPDATE_SUPPORT_USER_SUCCESS,
    supportUser,
  });

  return async (
    dispatch: ThunkDispatch<TAppState, IDependencies, TSupportUserAction | TCoreAction>,
    _,
  ): Promise<void> => {
    dispatch(request());
    try {
      let updatedSupportUser = await SupportUserService.updateSupportUser(supportUser, shouldRestore);

      if (supportUser.metadata) {
        updatedSupportUser = await SupportUserService.updateSupportUserMetadata(supportUser.id!, supportUser.metadata);
      }

      dispatch(success(updatedSupportUser));
      dispatch(closeModal());
      onFinish?.();
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updateFavorites = (id: string, favorite: TFavorite) => {
  const request = (): TAuthAction => ({
    type: SupportUserActionTypes.UPDATE_CURRENT_SUPPORT_USER_METADATA,
    id: id!,
    favoriteId: favorite.walletId,
  });

  const fail = (error: TAppError): TAuthAction => ({
    type: SupportUserActionTypes.UPDATE_CURRENT_SUPPORT_USER_METADATA_FAIL,
    id: id!,
    error,
    documentation: 'https://docs.treezor.com/guide/users/support.html#creation',
  });

  const success = (favorites: TFavorite[]): TAuthAction => ({
    type: SupportUserActionTypes.UPDATE_CURRENT_SUPPORT_USER_METADATA_SUCCESS,
    id,
    favorites,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<IAuthState, {}, TAuthAction>): Promise<void> => {
    dispatch(request());
    try {
      const updatedSupportUser: ISupportUser = await SupportUserService.updateCurrentSupportUserFavorites(
        id!,
        favorite,
      );
      dispatch(success(updatedSupportUser?.metadata?.favorites));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const removeSupportUser = (
  supportUser: ISupportUser,
  onFinish?: () => void,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction> => {
  const request = (): TSupportUserAction => ({
    type: SupportUserActionTypes.REMOVE_SUPPORT_USER,
  });

  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.REMOVE_SUPPORT_USER_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/users/support.html#deletion',
  });

  const success = (supportUserId: string): TSupportUserAction => ({
    type: SupportUserActionTypes.REMOVE_SUPPORT_USER_SUCCESS,
    supportUserId,
    isSuspended:
      supportUser.status === supportUserStatus.LOCK && supportUser.lockReason === supportUserLockReason.TEMPORARY_BLOCK,
  });

  return async (dispatch: ThunkDispatch<{}, IDependencies, TSupportUserAction>, _): Promise<void> => {
    dispatch(request());
    try {
      if (supportUser?.secureKey?.secureKeyId) {
        await Auth2FaService.deletePasskey(supportUser.secureKey.secureKeyId);
      }

      const supportUserId = await SupportUserService.removeSupportUser(supportUser);
      dispatch(success(supportUserId));
      onFinish?.();
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const searchSupportUser = (
  id: string,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction> => {
  const request = (): TSupportUserAction => ({
    type: SupportUserActionTypes.SEARCH_SUPPORT_USERS,
  });

  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.SEARCH_SUPPORT_USERS_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/users/support.html#creation',
  });

  const success = (supportUsers: ICursorElements<ISupportUser>): TSupportUserAction => ({
    type: SupportUserActionTypes.SEARCH_SUPPORT_USERS_SUCCESS,
    shouldSkipToaster: true,
    supportUsers,
  });

  return async (dispatch: ThunkDispatch<TAppState, IDependencies, TSupportUserAction>, _): Promise<void> => {
    dispatch(request());
    try {
      const supportUser = await SupportUserService.searchSupportUser(id);

      dispatch(success({ elements: [supportUser], cursor: {} }));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
export const unblockSupportUser = (
  supportUser: ISupportUser,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction> => {
  const request = (): TSupportUserAction => ({
    type: SupportUserActionTypes.UNBLOCK_SUPPORT_USER,
    id: supportUser.id,
  });

  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.UNBLOCK_SUPPORT_USER_FAIL,
    id: supportUser.id,
    error,
    // to add once the documentation is updated
    // documentation: 'https://docs.treezor.com/guide/users/support.html#deletion',
  });

  const success = (supportUser: ISupportUser): TSupportUserAction => ({
    type: SupportUserActionTypes.UNBLOCK_SUPPORT_USER_SUCCESS,
    supportUser,
  });

  return async (dispatch: ThunkDispatch<{}, IDependencies, TSupportUserAction>, _): Promise<void> => {
    dispatch(request());
    try {
      await SupportUserService.unblockSupportUser(supportUser);

      dispatch(success({ ...supportUser, wrongPwdAttempts: 0 }));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const suspendSupportUser = (
  supportUser: ISupportUser,
  onFinish?: () => void,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction> => {
  const request = (): TSupportUserAction => ({
    type: SupportUserActionTypes.SUSPEND_SUPPORT_USER,
    id: supportUser.id,
  });

  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.SUSPEND_SUPPORT_USER_FAIL,
    id: supportUser.id,
    error,
    // to add once the documentation is updated
    // documentation: 'https://docs.treezor.com/guide/users/support.html#deletion',
  });

  const success = (supportUser: ISupportUser): TSupportUserAction | TCoreAction => ({
    type: SupportUserActionTypes.SUSPEND_SUPPORT_USER_SUCCESS,
    supportUser,
  });

  return async (dispatch: ThunkDispatch<{}, IDependencies, TSupportUserAction | TCoreAction>, _): Promise<void> => {
    dispatch(request());
    try {
      const supportUserResponse = await SupportUserService.suspendSupportUser(supportUser);
      dispatch(success(supportUserResponse));
      dispatch(closeModal());
      onFinish?.();
      dispatch(getSupportUsers({ status: supportUserStatus.UNLOCK }));
      dispatch(getSuspendedSupportUsers());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const restoreSupportUserAccess = (
  supportUser: ISupportUser,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction> => {
  const request = (): TSupportUserAction => ({
    type: SupportUserActionTypes.RESTORE_SUPPORT_USER_ACCESS,
    id: supportUser.id,
  });

  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.RESTORE_SUPPORT_USER_ACCESS_FAIL,
    id: supportUser.id,
    error,
    // to add once the documentation is updated
    // documentation: 'https://docs.treezor.com/guide/users/support.html#deletion',
  });

  const success = (supportUser: ISupportUser): TSupportUserAction | TCoreAction => ({
    type: SupportUserActionTypes.RESTORE_SUPPORT_USER_ACCESS_SUCCESS,
    supportUser,
  });

  return async (dispatch: ThunkDispatch<{}, IDependencies, TSupportUserAction | TCoreAction>, _): Promise<void> => {
    dispatch(request());
    try {
      const supportUserResponse = await SupportUserService.restoreSupportUserAccess(supportUser);
      dispatch(success(supportUserResponse));
      dispatch(getSuspendedSupportUsers());
      dispatch(getSupportUsers({ status: supportUserStatus.UNLOCK }));
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const setNeedReview = (
  isReviewed: boolean,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction> => {
  const request = (): TSupportUserAction => ({
    type: SupportUserActionTypes.SET_REVIEW,
    isReviewed,
  });

  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.SET_REVIEW_FAIL,
    isReviewed,
    error,
  });

  const success = (): TSupportUserAction => ({
    type: SupportUserActionTypes.SET_REVIEW_SUCCESS,
    isReviewed,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<{}, IDependencies, TSupportUserAction>, _): Promise<void> => {
    dispatch(request());
    try {
      let allUsers = [];
      let cursor = undefined;
      let shouldFetchResults = true;

      while (shouldFetchResults) {
        const filterProps = cursor ? { cursor: cursor?.next } : {};
        const result = await SupportUserService.getSupportUsers({ status: 'UNLOCK', ...filterProps });
        cursor = result?.cursor;
        shouldFetchResults = !!cursor.next;
        allUsers = [...allUsers, ...result.elements];
      }

      const updateAllUsers = allUsers
        .filter((dashboardUser) => dashboardUser?.metadata?.role?.status !== advancedRoleStatus.VALIDATED)
        .map(async (dashboardUser) => {
          const advancedRole = getAdvandeRoleFromScopes(getSupportUserScopes(dashboardUser));
          const role: IAdvancedRoleMetadata = {
            slug: advancedRole.slug,
            status: isReviewed ? advancedRoleStatus.VALIDATED : advancedRoleStatus.PENDING,
            lastModified: Date.now().toString(),
            modifiedBy: getCurrentSupportUser()?.id,
          };
          await SupportUserService.updateSupportUserMetadata(dashboardUser.id, { role });
          return true;
        });

      await Promise.all(updateAllUsers);

      dispatch(success());
      dispatch(getSupportUsers());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const setShouldChangePassword = (shouldChangePassword: boolean): TAuthAction => ({
  type: SupportUserActionTypes.SHOULD_CHANGE_PASSWORD,
  shouldChangePassword,
});

export const requestChangePassword = (): TAuthAction => ({
  type: SupportUserActionTypes.CHANGE_PASSWORD,
});

export const cancelChangePassword = (error: TAppError): TAuthAction => ({
  type: SupportUserActionTypes.CHANGE_PASSWORD_FAIL,
  error,
  shouldSkipToaster: true,
});

export const changePassword = (
  supportUserId: string,
  payload: IChangePassword,
  onFinish?: () => void,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction> => {
  const fail = (error: TAppError): TAuthAction => ({
    type: SupportUserActionTypes.CHANGE_PASSWORD_FAIL,
    error,
  });

  const success = (supportUser: ISupportUser): TAuthAction => ({
    type: SupportUserActionTypes.CHANGE_PASSWORD_SUCCESS,
    supportUser,
  });

  return async (dispatch: ThunkDispatch<{}, IDependencies, TAuthAction>, _): Promise<void> => {
    dispatch(requestChangePassword());
    try {
      const supportUser = await SupportUserService.changePassword(supportUserId, payload);

      dispatch(success(supportUser));
      dispatch(setShouldChangePassword(false));
      onFinish?.();
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const requestUpdateAccessExpirationDate = (): TSupportUserAction => ({
  type: SupportUserActionTypes.UPDATE_EXPIRATION,
  shouldSkipToaster: true,
});

export const updateAccessExpirationDate = (
  supportUserId: string,
  onComplete?: () => void,
): ThunkAction<Promise<void>, TAppState, IDependencies, TSupportUserAction> => {
  const fail = (error: TAppError): TSupportUserAction => ({
    type: SupportUserActionTypes.UPDATE_EXPIRATION_FAIL,
    error,
  });

  const success = (): TSupportUserAction => ({
    type: SupportUserActionTypes.UPDATE_EXPIRATION_SUCCESS,
  });

  return async (dispatch: ThunkDispatch<{}, IDependencies, TSupportUserAction | TCoreAction>, _): Promise<void> => {
    dispatch(requestUpdateAccessExpirationDate());
    try {
      await SupportUserService.updateAccessExpirationDate(supportUserId);
      dispatch(success());
      dispatch(closeModal());
      onComplete?.();
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
